import BuildIcon from "@mui/icons-material/Build";
import * as React from "react";

import type { AgentThreadToolUseContent } from "@/shared/domain/agentThread/AgentThreadRecord";
import type { ClientTool } from "@/shared/domain/agentThread/clientTools";
import { confirmationContentFor } from "@/shared/domain/agentThread/clientTools";
import { toolInputForDisplay } from "@/shared/domain/agentThread/toolInput";
import { Button } from "@/shared/foundation/primitives";

import styles from "./ToolApprovalCard.module.css";
import { ToolApprovalDetailsModal } from "./ToolApprovalDetailsModal";
import { ToolRequestDisclosure } from "./ToolRequestDisclosure";

interface ToolApprovalCardProps {
  toolUse: AgentThreadToolUseContent;
  tool: ClientTool;
  onApprove: () => void;
  onDecline: () => void;
  /**
   * Turns on automatic mode. The caller supplies it only when that would stop
   * this tool asking next time; a tool pinned to REQUIRE_APPROVAL prompts
   * whatever Auto is set to, and offering to change that would be a lie.
   */
  onEnableAutoMode?: () => void;
}

/**
 * Inline approval card rendered in the thread message stream when a
 * client-side tool requires user confirmation before executing.
 *
 * The tool's `ConfirmationContent` preview renders inline on the card itself.
 * This is the default and lowest-friction approval path, so the facts a user
 * needs to judge the request — what is being created, in which collection,
 * with which members — are visible before Approve is clicked. When no preview
 * applies, the card falls back to {@link ToolRequestDisclosure}, which keeps
 * the raw request one click away. "More details" opens that same preview in a
 * roomier modal.
 */
export function ToolApprovalCard({
  toolUse,
  tool,
  onApprove,
  onDecline,
  onEnableAutoMode,
}: ToolApprovalCardProps) {
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  // Latched once the user decides, and passed down to the details modal.
  // `approve()` is async and the card stays mounted until the runner advances,
  // so every control that could take a second decision has to go quiet — an
  // enabled button whose handler returns early is a promise the UI doesn't keep.
  const [resolved, setResolved] = React.useState(false);
  const cardRef = React.useRef<HTMLDivElement>(null);

  // Scroll the card into view when it first appears. Use block: "start"
  // so the card appears at the top of the viewport, above the scroll-down
  // button which sits at the bottom of the scroll container.
  React.useEffect(() => {
    cardRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, []);

  const params = toolInputForDisplay(toolUse.input);
  const Preview = confirmationContentFor(tool, params);
  // Gated on the resolved preview rather than on the tool merely declaring one,
  // so "More details" only ever opens a roomier view of what the card already
  // shows. Gated on `tool.ConfirmationContent`, a guard-rejected proposal would
  // open a modal duplicating the card's own raw-request disclosure.
  const hasPreview = Preview !== undefined;

  const handleApprove = () => {
    if (resolved) {
      return;
    }
    setResolved(true);
    onApprove();
  };

  const handleDecline = () => {
    if (resolved) {
      return;
    }
    setResolved(true);
    onDecline();
  };

  return (
    <div className={styles.container} ref={cardRef}>
      <div className={styles.card}>
        <div className={styles.header}>
          <BuildIcon className={styles.titleIcon} />
          <span className={styles.titleText}>
            <b>{tool.displayName}</b> wants to run
          </span>
        </div>

        {/* The card's one scroll region — see the module CSS. It takes the
            tabindex the ConfirmationPanel used to, since the scrolling moved
            out here. */}
        <div className={styles.preview} tabIndex={0}>
          {Preview ? (
            <Preview params={params} />
          ) : (
            // The tool declares no preview, or its guard rejected these params.
            <ToolRequestDisclosure params={params} />
          )}
        </div>

        {hasPreview && (
          <button
            type="button"
            className={styles.detailsLink}
            onClick={() => setDetailsOpen(true)}
            disabled={resolved}
          >
            More details
          </button>
        )}

        {onEnableAutoMode && (
          <button
            type="button"
            className={styles.autoModeHint}
            onClick={onEnableAutoMode}
            disabled={resolved}
          >
            Turn on Auto to run {tool.displayName} without asking
          </button>
        )}

        <div className={styles.actions}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleDecline}
            disabled={resolved}
          >
            Decline
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleApprove}
            disabled={resolved}
          >
            Approve
          </Button>
        </div>
      </div>

      {hasPreview && (
        <ToolApprovalDetailsModal
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          toolUse={toolUse}
          tool={tool}
          resolved={resolved}
          onApprove={handleApprove}
          onDecline={handleDecline}
        />
      )}
    </div>
  );
}
